import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, Check, AlertCircle } from "lucide-react";
import { useT } from "@/lib/i18n";
import LangSwitcher from "@/components/LangSwitcher";
import RatchetLogo from "@/components/RatchetLogo";

export default function AcceptInvite() {
  const t = useT();
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/team/invites/${token}/preview`)
      .then(({ data }) => setPreview(data))
      .catch((e) => setError(e?.response?.data?.detail || "Invalid invite"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/team/invites/accept", { token, name, password });
      localStorage.setItem("ratchet_token", data.token);
      await refresh();
      nav("/app");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to accept invite");
    } finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="surface p-10 max-w-md w-full text-center" data-testid="invite-error">
          <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl tracking-tight mb-2">Invite invalid</h1>
          <p className="text-white/60 text-sm">{error}</p>
          <Link to="/login" className="btn-ghost mt-6 inline-flex">Go to login</Link>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60 font-mono text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading invite...
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between p-12 w-1/2 border-r border-white/5 bg-gradient-to-br from-black via-[#050A1A] to-black">
        <Link to="/" className="flex items-center gap-2">
          <RatchetLogo size={28} />
          <span className="font-display font-black tracking-tight">RATCHET</span>
        </Link>
        <div>
          <div className="overline mb-3">You've been invited</div>
          <p className="font-display text-2xl tracking-tight leading-snug max-w-md">
            Join <span className="text-primary">{preview.org_name}</span> on Ratchet.
          </p>
          <p className="text-sm text-white/40 mt-4 font-mono">
            role · {preview.role}<br/>
            plan · {preview.org_plan}<br/>
            email · {preview.email}
          </p>
        </div>
        <div className="overline">secure invite · expires in 7d</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6"><LangSwitcher /></div>
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="invite-form">
          <div>
            <div className="overline mb-2">Accept invite</div>
            <h1 className="font-display font-black text-3xl tracking-tighter">Create your account</h1>
            <p className="text-white/50 text-sm mt-2">Joining <span className="font-mono text-primary">{preview.org_name}</span></p>
          </div>
          <div>
            <label className="overline block mb-1.5">Email</label>
            <input value={preview.email} disabled
              className="w-full bg-white/[0.02] border border-white/10 px-3 py-2.5 text-sm font-mono text-white/50"
              data-testid="invite-email-display" />
          </div>
          <div>
            <label className="overline block mb-1.5">{t("auth.signup.yourName")}</label>
            <input required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm focus:border-primary focus:outline-none"
              data-testid="invite-name-input" />
          </div>
          <div>
            <label className="overline block mb-1.5">{t("auth.signup.pwd")}</label>
            <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm focus:border-primary focus:outline-none font-mono"
              data-testid="invite-password-input" />
          </div>
          <button disabled={submitting} className="btn-primary w-full" data-testid="invite-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Join workspace <Check className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
